<template>
	<div class="w-100 p-2 panel">
		<!-- Header -->
		<div class="header" :class="{ hideTab: allowEditing }" v-show="!isFullscreen">
			<div class="searchContainer">
				<b-input-group class="search-group">
					<b-input-group-prepend>
						<b-input-group-text>
							<b-icon icon="search" />
						</b-input-group-text>
					</b-input-group-prepend>
					<b-form-input id="search-table" type="text" v-model="filter"></b-form-input>
				</b-input-group>
			</div>
			<b-icon-arrows-fullscreen class="fullscreen-icon" @click="goFullscreen" />
		</div>

		<!-- Fullscreen controls -->
		<div v-if="isFullscreen" class="fullscreenIndicatorContainer">
			<div class="dashboardFullScreenButtons">
				<div class="fullscreenClockContainer d-flex align-items-center pl-2">
					<b-icon-stopwatch class="mr-1" />
					<small>{{ time }}</small>
				</div>
				<div class="btnZoomOut" @click="zoomOut" :style="zoomOutDisabled" :title="$i18n.t('zoomOut')">
					<b-icon-zoom-out />
				</div>
				<div class="btnZoomOut" @click="zoomIn" :style="zoomInDisabled" :title="$i18n.t('zoomIn')">
					<b-icon-zoom-in />
				</div>
				<div class="btnZoomOut" @click="exitFullScreen"><b-icon-x-lg /></div>
				<div class="btnZoomOut" :title="$i18n.t('resetZoom')" @click="resetZoom"><b-icon-arrow-counterclockwise />
				</div>
				<div class="btnZoomOut" @click="adjustScreen"><b-icon-arrows-fullscreen /></div>
				<div class="fullscreenUserContainer pr-2">
					<b-icon-person-circle class="mr-1" />
					<small>{{ $session.get('user').nickname }}</small>
				</div>
			</div>
			<div class="fullscreenIndicator" @click="toggleOpenFullscreenButtons"><b-icon-triangle /></div>
		</div>

		<!-- Display picker -->
		<div v-if="isFullscreen && isMouseMoving" class="dashboardDisplayToggleContainer" :style="{ '--zoom': zoomLevel }"
			:class="{ open: isDashboardDisplayListOpen }">
			<div class="dashboardDisplayToggleContainerList">
				<div v-for="item in dashboardDisplayList" :key="item.id" class="dashboardDisplayItem"
					:class="{ selected: item.id == selectedDashboardDisplayId }" @click="selectDashboardDisplay(item.id)">
					<div class="dashboardDisplayItemInfo">
						<b-icon-check v-if="item.id == selectedDashboardDisplayId" />
						<strong>{{ item.name }}</strong>
					</div>
					<button
						v-if="item.id == selectedDashboardDisplayId && item.id != 'default' && dashboardDisplayBeingUpdated == -1"
						class="selectedDashboardDisplayRefreshButton" @click.stop="refreshDashboardDisplay(item.id)">
						<b-icon-arrow-clockwise />
					</button>
					<b-spinner v-else-if="dashboardDisplayBeingUpdated == item.id" class="dashboardDisplayRefreshSpinner" />
				</div>
			</div>
			<button id="dashboardDisplayButton" @click="isDashboardDisplayListOpen = !isDashboardDisplayListOpen">
				<b-icon-columns />
			</button>
		</div>

		<!-- Grid area -->
		<div id="dashboardArea" ref="dashboardArea" class="w-100" :key="groupsKey">
			<draggable :list="groupsToShow" handle=".handle"
				:class="{ dashboardPanelGrid: hasDashboardDisplay && isFullscreen && selectedDashboardDisplayId != 'default' }"
				:style="selectedDisplayStyle">
				<group-component v-for="(group, idx) in groupsToShow" :key="group.id" :style="getGroupPosition(group)"
					:data="group" :ref="`Group-${idx}`" :isFullscreen="isFullscreen" :zoomLevel="zoomLevel"
					@update="updatePosition">
					<DashboardGridLayout v-if="!refreshGrid" :dashboardId="id" :id="group.id" :groupEl="group"
						:isShowingAllIcons="isShowingAllIcons && !allowEditing" :alarmColors="alarmColors"
						:hideFooter="isFullscreen" :zoomLevel="zoomLevel" :cols="getNumberOfCols(group)"
						:rows="getNumberOfRows(group)" :searchVariableValueById="searchVariableValueById" :filter="filter"
						@change="gridChange(...arguments, groups.indexOf(group))" @alarmActive="alarmActive"
						@removeAlarm="removeAlarm" @loaded="$emit('loaded')" @record="showRecordModal" ref="grid" />
				</group-component>
			</draggable>
		</div>

		<!-- Record modal -->
		<mtw-modal :width="'90vw'" :height="'90vh'" :visible="showRecordVisibility" :zIndex="1000"
			@close="() => { showRecordVisibility = false }">
			<mosaic-container :dialogView="true" :preSelectedVideoConfigs="selectedVideoConfigs" />
		</mtw-modal>
	</div>
</template>
  
<script>
import draggable from 'vuedraggable';
import GroupComponent from '../../group/GroupComponent/index.vue';
import DashboardGridLayout from '../DashboardGrid/index.vue';

// ✅ short: behavior lives in mixins
import fullscreen from './mixins/fullscreen';
import zoom from './mixins/zoom';
import dashboardDisplay from './mixins/dashboardDisplay';
import gridLayout from './mixins/gridLayout';
import dataServices from './mixins/dataServices';
import variables from './mixins/variables';
import alarms from './mixins/alarms';
import records from './mixins/records';

export default {
	name: 'DashboardPanel',
	components: { draggable, GroupComponent, DashboardGridLayout },
	mixins: [fullscreen, zoom, dashboardDisplay, gridLayout, dataServices, variables, alarms, records],

	props: {
		id: { type: [String, Number], default: 0 },
		fullscreen: { type: Boolean, default: false },
		zoom: { type: Number, default: 100 },
		initialDashboardDisplay: { type: Number, default: 0 },
	},

	data() {
		return {
			isShowingAllIcons: true,
			refreshGrid: false,
			groupsKey: 0,
			filter: "",
		};
	},

	computed: {
		allowEditing() {
			return this.$session.get('user').privilege == 2 || this.$session.get('isMobile');
		},
		alarmColors() {
			const { attentionColor, informColor, criticalColor } = this.dashboard || {};
			return { attention: attentionColor, inform: informColor, critical: criticalColor };
		},
	},

	methods: {
		async reloadItems() {
			await this.service.searchByUserId(this.id, this.$session.get('logged_id'))
				.then(async (dashboard) => {
					this.dashboard = dashboard;
					this.$session.set('currentDashboard', dashboard);
					this.groups = dashboard.groups;
					this.groupsKey += 1;
				});
		},
	},

	async mounted() {
		// guard: user access to dashboard
		const avail = this.$session.get('user').dashboards.map(d => d.id);
		if (!avail.includes(parseInt(this.id))) {
			this.$router.push('/dashboard').catch(() => { });
			return;
		}

		await this.reloadItems();

		// honor initial fullscreen/zoom props (mixins handle the rest)
		if (this.fullscreen) {
			this.isFullscreen = true;
			if (!this.selectedDashboardDisplayId || this.selectedDashboardDisplayId <= 0) this.zoomLevel = this.zoom;
		} else {
			this.zoomLevel = 100;
		}

		this.isLoading = false;
		this.$emit('loaded');
	},

	beforeDestroy() {
		// small safeguard; mixins clean up their own listeners/intervals
		this.zoomLevel = 100;
	},
};
</script>
  

<style lang="scss" scoped src="./style.scss"/>